import React, { useState, useEffect } from 'react';
import { onAuthChange, logOut } from './services/auth.js';
import { subscribeToUserProfile, createUserProfile, updateUserProfile, subscribeToFriendRequests } from './services/firestore.js';
import Navigation from './components/Navigation.js';
import Feed from './pages/Feed.js';
import Chat from './pages/Chat.js';
import MuseAI from './components/MuseAI.js';
import Profile from './pages/Profile.js';
import Leaderboard from './pages/Leaderboard.js';
import PublicProfile from './pages/PublicProfile.js';
import AdminDashboard from './pages/AdminDashboard.js';
import Login from './pages/Login.js';
import BannedPage from './pages/BannedPage.js';
import IssueReport from './pages/IssueReport.jsx';

import CommunityGuidelines from './pages/CommunityGuidelines.js';
import Mission from './pages/Mission.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import TermsOfService from './pages/TermsOfService.jsx';
import ContactUs from './pages/ContactUs.jsx';
import SeedEmojis from './pages/SeedEmojis.jsx';
import Support from './pages/Support.jsx';
import NotFound from './pages/NotFound.js';
import GiftNotificationModal from './components/GiftNotificationModal.jsx';
import AdminEmoteManager from './pages/AdminEmoteManager.js';
import OnboardingTutorial from './components/OnboardingTutorial.jsx';
import Subscription from './pages/Subscription.jsx';
import GiftSuccess from './pages/GiftSuccess.jsx';
import SubscriptionSync from './components/SubscriptionSync.jsx';
import { useAppearance } from './hooks/useAppearance.js';

// Mobile Control Icons
import { Home, LogOut, MessageSquare, Menu, X, Bell, LayoutDashboard, Settings, Smartphone, Sun, Moon } from 'lucide-react';
import AppearanceSettingsModal from './components/AppearanceSettingsModal.jsx';

export default function App() {
    const { appearance } = useAppearance();
    const [activeTab, setActiveTab] = useState('feed');
    const [tabData, setTabData] = useState({}); // To pass data to pages
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // --- MOBILE CONTROLS STATE ---
    const [notifications, setNotifications] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

    // Subscribe to Friend Requests (Mobile Only Logic)
    useEffect(() => {
        if (!user?.uid) return;
        const unsubscribe = subscribeToFriendRequests(user.uid, (reqs) => setNotifications(reqs));
        return () => unsubscribe();
    }, [user]);

    // Theme Toggle (Mobile)
    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
    };

    // Apply GLOBAL Appearance Settings
    useEffect(() => {
        const { fontSize, emoteSize } = appearance.global;
        document.documentElement.style.setProperty('--global-font-size', `${fontSize}px`);
        document.documentElement.style.setProperty('--global-emote-size', `${emoteSize}px`);
    }, [appearance.global]);

    // Listen to auth state changes and user profile updates
    useEffect(() => {
        // Check for Gift Success Redirect OR Generic Deep Links
        const params = new URLSearchParams(window.location.search);

        // Priority 1: Gift Success
        if (params.get('session_id')) {
            setActiveTab('gift-success');
        }
        // Priority 2: Spark Deep Link
        else if (params.get('spark')) {
            const sparkId = params.get('spark');
            setActiveTab('feed');
            setTabData({ initialSparkId: sparkId });
        }
        // Priority 3: Generic Deep Link (e.g. ?tab=report-issue)
        else if (params.get('tab')) {
            const requestedTab = params.get('tab');
            setActiveTab(requestedTab);
        }

        // Safety Timeout: If auth hangs for 15s, force stop loading (Will show Login)
        const safetyTimer = setTimeout(() => {
            if (loading) {
                console.warn("Auth check timed out, forcing load completion.");
                setLoading(false);
            }
        }, 15000);

        let userUnsubscribe = null;

        const authUnsubscribe = onAuthChange((firebaseUser) => {
            clearTimeout(safetyTimer); // Clear timeout if auth responds

            // Clean up previous user subscription if exists
            if (userUnsubscribe) {
                userUnsubscribe();
                userUnsubscribe = null;
            }

            if (firebaseUser) {
                // Subscribe to real-time user profile updates
                userUnsubscribe = subscribeToUserProfile(firebaseUser.uid, async (userData) => {
                    if (userData) {
                        // REPAIR: Check if stats are missing (legacy users)
                        if (!userData.stats) {
                            console.log('Repairing user profile: missing stats');
                            await updateUserProfile(userData.id, {
                                stats: {
                                    sparksCreated: 0,
                                    likesReceived: 0,
                                    challengesCompleted: 0,
                                    museUsage: 0
                                }
                            });
                            // The subscription will fire again with the update
                        } else {
                            setUser(userData);
                        }
                    } else {
                        // Profile missing! Create it now.
                        console.log('User profile not found in Firestore, creating now...');
                        const newProfile = {
                            uid: firebaseUser.uid,
                            name: firebaseUser.displayName || 'User',
                            email: firebaseUser.email,
                            avatar: firebaseUser.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${firebaseUser.email}`,
                            bio: '',
                            stats: {
                                sparksCreated: 0,
                                likesReceived: 0,
                                challengesCompleted: 0,
                                museUsage: 0
                            },
                            badges: [],
                            socialLinks: {},
                            isAdmin: false,
                            isSuperAdmin: false,
                            isBanned: false,
                            hasSeenTutorial: false // Initialize tutorial state
                        };

                        try {
                            await createUserProfile(firebaseUser.uid, newProfile);
                            // Subscription will catch the creation
                        } catch (err) {
                            console.error("Error creating missing profile:", err);
                            // Fallback to temp user
                            setUser(newProfile);
                        }
                    }
                    setLoading(false);
                });
            } else {
                // User is signed out
                setUser(null);
                setLoading(false);
            }
        });

        return () => {
            clearTimeout(safetyTimer);
            authUnsubscribe();
            if (userUnsubscribe) userUnsubscribe();
        };
    }, []);

    const handleViewProfile = (userId) => {
        setSelectedUserId(userId);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedUserId(null); // Close public profile when switching tabs
    };

    const handleLoginSuccess = () => {
        // Auth state listener will handle updating user automatically
        // No need to set loading here as it creates a race condition
    };

    const handleLogout = async () => {
        await logOut();
    };

    // Show loading screen while checking auth
    if (loading) {
        return React.createElement('div', {
            style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'var(--bg-dark)',
                color: 'white'
            }
        },
            React.createElement('div', { style: { textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: '48px', marginBottom: '20px' } }, '✨'),
                React.createElement('h2', { style: { fontSize: '24px' } }, 'Loading Spark Inspo...')
            )
        );
    }

    // Show login page if not authenticated
    if (!user) {
        return React.createElement(Login, { onLoginSuccess: handleLoginSuccess });
    }

    // REDIRECT BANNED USERS
    if (user.isBanned) {
        return React.createElement(BannedPage, { user: user });
    }

    // User is authenticated, show main app
    const renderContent = () => {
        // If viewing another user's profile, show PublicProfile
        if (selectedUserId) {
            return React.createElement(PublicProfile, {
                userId: selectedUserId,
                onBack: () => setSelectedUserId(null),
                user: user
            });
        }

        switch (activeTab) {
            case 'feed': return React.createElement(Feed, { onViewProfile: handleViewProfile, user: user, initialSparkId: tabData.initialSparkId });
            case 'chat': return React.createElement(Chat, { onViewProfile: handleViewProfile, user: user });
            case 'muse': return React.createElement(MuseAI, { user: user });
            case 'profile': return React.createElement(Profile, { onLogout: handleLogout, user: user });
            case 'leaderboard': return React.createElement(Leaderboard, { onViewProfile: handleViewProfile, user: user });
            case 'support':
                return React.createElement(Support, { user: user });
            case 'subscription':
                return React.createElement(Subscription, { user: user });
            case 'gift-success': return React.createElement(GiftSuccess, { user: user, onNavigate: handleTabChange });
            case 'admin': return React.createElement(AdminDashboard, { user: user, onNavigate: handleTabChange, onViewProfile: handleViewProfile });

            // AdSense / Legal Pages
            case 'mission': return React.createElement(Mission, { onNavigate: handleTabChange });
            case 'privacy': return React.createElement(PrivacyPolicy);
            case 'terms': return React.createElement(TermsOfService);
            case 'contact': return React.createElement(ContactUs);
            case 'guidelines': return React.createElement(CommunityGuidelines);
            case 'report-issue': return React.createElement(IssueReport, { user: user, onNavigate: handleTabChange });

            // Admin Tools
            case 'seed-emojis': return React.createElement(SeedEmojis, { user: user, onNavigate: handleTabChange });
            case 'manage-emotes': return React.createElement(AdminEmoteManager, { onBack: () => handleTabChange('admin') });

            case '404':
            case 'not-found':
                return React.createElement(NotFound, { onNavigate: handleTabChange });

            default: return React.createElement(Feed, { onViewProfile: handleViewProfile, user: user });
        }
    };



    // ... existing code ...

    return React.createElement('div', { className: 'app-container' },
        // Landscape Warning Overlay
        React.createElement('div', { className: 'landscape-warning' },
            React.createElement(Smartphone, { size: 64, className: 'landscape-icon' }),
            React.createElement('div', { className: 'landscape-text' },
                "Please rotate your device to portrait mode for the best experience."
            )
        ),

        // Floating Mobile Logo (Moved here to avoid clipping in Nav)
        React.createElement('div', { className: 'mobile-floating-logo-container' },
            React.createElement('img', {
                src: "/images/logo.png",
                alt: "SparkInspo",
                className: "mobile-floating-logo"
            })
        ),

        // --- NEW MOBILE TOP CONTROLS (Vertical Stack) ---
        React.createElement('div', { className: 'mobile-top-controls' },
            // Notifications
            React.createElement('button', {
                onClick: () => setActiveTab('profile'), // Go to profile for notifs
                className: 'mobile-control-btn',
                style: { position: 'relative' }
            },
                React.createElement(Bell, { size: 16 }),
                notifications.length > 0 && React.createElement('span', {
                    style: {
                        position: 'absolute', top: '4px', right: '4px',
                        background: 'var(--secondary)', width: '6px', height: '6px',
                        borderRadius: '50%', border: '1px solid var(--bg-dark)'
                    }
                })
            ),
            // Settings
            React.createElement('button', {
                onClick: () => setShowSettings(true),
                className: 'mobile-control-btn'
            }, React.createElement(Settings, { size: 16 })),
            // Theme
            React.createElement('button', {
                onClick: toggleTheme,
                className: 'mobile-control-btn'
            }, React.createElement(theme === 'dark' ? Sun : Moon, { size: 16 }))
        ),
        showSettings && React.createElement(AppearanceSettingsModal, { onClose: () => setShowSettings(false) }),

        React.createElement('div', {
            className: `sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`
        },
            React.createElement(Navigation, {
                activeTab: selectedUserId ? null : activeTab,
                onTabChange: handleTabChange,
                user: user,
                isCollapsed: isSidebarCollapsed,
                onToggleCollapse: () => setIsSidebarCollapsed(!isSidebarCollapsed)
            })
        ),
        React.createElement('div', { className: 'main-content' },
            user.giftNotificationPending && React.createElement(GiftNotificationModal, {
                user: user,
                onClose: () => {
                    // Optimistic update locally to remove modal immediately visual-wise
                    setUser(prev => ({ ...prev, giftNotificationPending: false }));
                }
            }),
            renderContent(),
            user && user.hasSeenTutorial === false && React.createElement(OnboardingTutorial, {
                user: user,
                onNavigate: handleTabChange,
                onClose: () => {
                    setUser(prev => ({ ...prev, hasSeenTutorial: true }));
                }
            }),

            /* AUTO-SYNC STRIPE SUBSCRIPTION STATUS */
            user && React.createElement(SubscriptionSync, { user: user })
        )
    );
}
